// src/pages/Admin/ShippingManagement.js 
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Grid
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Save as SaveIcon, 
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { supabase } from '../../services/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

const ShippingManagement = () => {
  const { isAdmin } = useAuth();
  const [shippingPrices, setShippingPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null, price: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: null });
  const [createDialog, setCreateDialog] = useState({ open: false });
  const [newGovernorate, setNewGovernorate] = useState({
    governorate_name_ar: '',
    governorate_name_en: '',
    shipping_price: ''
  });

  // RTL TextField styling - exactly the same as ProductsFilter
  const rtlTextFieldSx = {
    '& .MuiOutlinedInput-root': {
      '& .MuiOutlinedInput-notchedOutline legend': {
        textAlign: 'right',
        transformOrigin: 'top right',
        right: 12,
        left: 'auto'
      }
    },
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
      fontWeight: 'bold'
    }
  };

  // RTL Table Cell styling for proper alignment and spacing
  const rtlTableCellSx = {
    textAlign: 'right',
    direction: 'rtl',
    padding: '16px 24px',
    '&:not(:last-child)': {
      borderRight: '1px solid rgba(224, 224, 224, 1)',
    },
    '&:last-child': {
      paddingRight: '24px',
    },
    '&:first-of-type': {
      paddingLeft: '24px',
    }
  };

  // Header cell styling
  const rtlHeaderCellSx = {
    ...rtlTableCellSx,
    fontWeight: 'bold',
    color: 'white',
    backgroundColor: 'primary.main'
  };

  // Fetch shipping prices
  const fetchShippingPrices = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('shipping_prices')
        .select('*')
        .order('governorate_name_ar');

      if (error) throw error;
      setShippingPrices(data || []);
    } catch (err) {
      setError('خطأ في تحميل أسعار الشحن: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Update shipping price
  const updateShippingPrice = async (id, newPrice) => {
    try {
      const { error } = await supabase
        .from('shipping_prices')
        .update({ shipping_price: parseFloat(newPrice) })
        .eq('id', id);

      if (error) throw error;

      setShippingPrices(prev =>
        prev.map(item =>
          item.id === id ? { ...item, shipping_price: parseFloat(newPrice) } : item
        )
      );

      setSuccess('تم تحديث سعر الشحن بنجاح');
      setEditingId(null);
      setEditPrice('');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('خطأ في تحديث سعر الشحن: ' + err.message);
    }
  };

  // Handle edit start
  const handleEditStart = (id, currentPrice) => {
    setEditingId(id);
    setEditPrice(currentPrice.toString());
  };

  // Handle edit cancel
  const handleEditCancel = () => {
    setEditingId(null);
    setEditPrice('');
  };

  // Handle edit save
  const handleEditSave = (id) => {
    if (!editPrice || parseFloat(editPrice) < 0) {
      setError('يرجى إدخال سعر صحيح');
      return;
    }

    setConfirmDialog({
      open: true,
      id: id,
      price: editPrice
    });
  };

  // Handle confirm update
  const handleConfirmUpdate = () => {
    updateShippingPrice(confirmDialog.id, confirmDialog.price);
    setConfirmDialog({ open: false, id: null, price: null });
  };

  // Handle delete governorate
  const handleDeleteGovernorate = async (id) => {
    try {
      const { error } = await supabase
        .from('shipping_prices')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setShippingPrices(prev => prev.filter(item => item.id !== id));
      setSuccess('تم حذف المحافظة بنجاح');
      setDeleteDialog({ open: false, id: null, name: null });
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('خطأ في حذف المحافظة: ' + err.message);
    }
  };

  // Handle create new governorate
  const handleCreateGovernorate = async () => {
    if (!newGovernorate.governorate_name_ar || !newGovernorate.governorate_name_en || !newGovernorate.shipping_price) {
      setError('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    if (parseFloat(newGovernorate.shipping_price) < 0) {
      setError('يرجى إدخال سعر شحن صحيح');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('shipping_prices')
        .insert([{
          governorate_name_ar: newGovernorate.governorate_name_ar,
          governorate_name_en: newGovernorate.governorate_name_en,
          shipping_price: parseFloat(newGovernorate.shipping_price)
        }])
        .select();

      if (error) throw error;

      setShippingPrices(prev => [...prev, ...data].sort((a, b) => 
        a.governorate_name_ar.localeCompare(b.governorate_name_ar)
      ));
      
      setSuccess('تم إضافة المحافظة بنجاح');
      setCreateDialog({ open: false });
      setNewGovernorate({
        governorate_name_ar: '',
        governorate_name_en: '',
        shipping_price: ''
      });
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('خطأ في إضافة المحافظة: ' + err.message);
    }
  };

  // Reset create form
  const resetCreateForm = () => {
    setCreateDialog({ open: false });
    setNewGovernorate({
      governorate_name_ar: '',
      governorate_name_en: '',
      shipping_price: ''
    });
  };

  useEffect(() => {
    if (isAdmin) {
      fetchShippingPrices();
    }
  }, [isAdmin]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', direction: 'rtl' }}>
        <Typography variant="h6" color="error">
          غير مصرح لك بالوصول إلى هذه الصفحة
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, direction: 'rtl' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, direction: 'rtl' }}>
      {/* Header with Add Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          إدارة أسعار الشحن
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialog({ open: true })}
          sx={{ borderRadius: 2, fontWeight: 'bold', gap: 0.5 }}
        >
          إضافة محافظة جديدة
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      
      {/* Shipping Prices Table */}
      <TableContainer 
        component={Paper} 
        sx={{ 
          mt: 2,
          direction: 'rtl',
          '& .MuiTable-root': {
            direction: 'rtl'
          },
          // Enhanced mobile responsiveness
          '@media (max-width: 600px)': {
            '& .MuiTableCell-root': {
              padding: '8px 12px',
              fontSize: '0.875rem'
            }
          }
        }}
      >
        <Table sx={{ direction: 'rtl' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={rtlHeaderCellSx}>
                المحافظة (العربية)
              </TableCell>
              <TableCell sx={rtlHeaderCellSx}>
                المحافظة (الإنجليزية)
              </TableCell>
              <TableCell sx={rtlHeaderCellSx}>
                سعر الشحن (جنيه)
              </TableCell>
              <TableCell sx={rtlHeaderCellSx}>
                الإجراءات
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shippingPrices.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell sx={rtlTableCellSx}>
                  <Typography variant="body1" fontWeight="bold">
                    {row.governorate_name_ar}
                  </Typography>
                </TableCell>
                <TableCell sx={{
                  ...rtlTableCellSx,
                  textAlign: 'left',
                  direction: 'ltr'
                }}>
                  <Typography variant="body1" fontWeight="bold">
                    {row.governorate_name_en}
                  </Typography>
                </TableCell>
                <TableCell sx={rtlTableCellSx}>
                  {editingId === row.id ? (
                    <TextField
                      value={editPrice}
                      onChange={(e) => setEditPrice(e.target.value)}
                      type="number"
                      size="small"
                      inputProps={{ min: 0, step: 0.5 }}
                      sx={{ 
                        width: 100,
                        ...rtlTextFieldSx
                      }}
                    />
                  ) : (
                    <Chip
                      label={`${parseFloat(row.shipping_price).toFixed(2)} جنيه`}
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                </TableCell>
                <TableCell sx={rtlTableCellSx}>
                  {editingId === row.id ? (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleEditSave(row.id)}
                        title="حفظ"
                      >
                        <SaveIcon />
                      </IconButton>
                      <IconButton
                        color="secondary"
                        size="small"
                        onClick={handleEditCancel}
                        title="إلغاء"
                      >
                        <CancelIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleEditStart(row.id, row.shipping_price)}
                        title="تعديل"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => setDeleteDialog({ 
                          open: true, 
                          id: row.id, 
                          name: row.governorate_name_ar 
                        })}
                        title="حذف"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Confirmation Dialog */}
      <Dialog 
        open={confirmDialog.open} 
        onClose={() => setConfirmDialog({ open: false, id: null, price: null })}
        sx={{ direction: 'rtl' }}
      >
        <DialogTitle>تأكيد التحديث</DialogTitle>
        <DialogContent>
          <Typography>
            هل أنت متأكد من تحديث سعر الشحن إلى {confirmDialog.price} جنيه؟
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'row-reverse' }}>
          <Button onClick={handleConfirmUpdate} variant="contained" color="primary">
            تأكيد
          </Button>
          <Button onClick={() => setConfirmDialog({ open: false, id: null, price: null })}>
            إلغاء
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialog.open} 
        onClose={() => setDeleteDialog({ open: false, id: null, name: null })}
        sx={{ direction: 'rtl' }}
      >
        <DialogTitle sx={{ color: 'error.main' }}>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <Typography>
            هل أنت متأكد من حذف محافظة "{deleteDialog.name}"؟
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            تحذير: هذا الإجراء لا يمكن التراجع عنه
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'row-reverse' }}>
          <Button 
            onClick={() => handleDeleteGovernorate(deleteDialog.id)} 
            variant="contained" 
            color="error"
          >
            حذف
          </Button>
          <Button onClick={() => setDeleteDialog({ open: false, id: null, name: null })}>
            إلغاء
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create New Governorate Dialog */}
      <Dialog 
        open={createDialog.open} 
        onClose={resetCreateForm}
        maxWidth="sm"
        fullWidth
        sx={{ direction: 'rtl' }}
      >
        <DialogTitle>إضافة محافظة جديدة</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="اسم المحافظة بالعربية"
                value={newGovernorate.governorate_name_ar}
                onChange={(e) => setNewGovernorate(prev => ({
                  ...prev,
                  governorate_name_ar: e.target.value
                }))}
                InputLabelProps={{
                  sx: {
                    fontWeight: 'bold',
                    left: 'auto',
                    '&:not(.MuiInputLabel-shrink)': {
                      right: 30,   
                    },
                    '&.MuiInputLabel-shrink': {
                      right: 1,
                      transform: 'translateX(17px) translateY(-7px) scale(0.75)',
                    },
                    '&.Mui-required': {
                      '&::after': {
                        display: 'none'
                      }
                    }
                  }
                }}
                sx={rtlTextFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="اسم المحافظة بالإنجليزية"
                value={newGovernorate.governorate_name_en}
                onChange={(e) => setNewGovernorate(prev => ({
                  ...prev,
                  governorate_name_en: e.target.value
                }))}
                InputLabelProps={{
                  sx: {
                    fontWeight: 'bold',
                    left: 'auto',
                    '&:not(.MuiInputLabel-shrink)': {
                      right: 30,   
                    },
                    '&.MuiInputLabel-shrink': {
                      right: 1,
                      transform: 'translateX(20px) translateY(-7px) scale(0.75)',
                    },
                    '&.Mui-required': {
                      '&::after': {
                        display: 'none'
                      }
                    }
                  }
                }}
                sx={{
                  ...rtlTextFieldSx,
                  '& .MuiInputBase-input': {
                    textAlign: 'left',
                    direction: 'ltr',
                    fontWeight: 'bold'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="سعر الشحن (جنيه)"
                type="number"
                value={newGovernorate.shipping_price}
                onChange={(e) => setNewGovernorate(prev => ({
                  ...prev,
                  shipping_price: e.target.value
                }))}
                inputProps={{ min: 0, step: 0.5 }}
                InputLabelProps={{
                  sx: {
                    fontWeight: 'bold',
                    left: 'auto',
                    '&:not(.MuiInputLabel-shrink)': {
                      right: 30,   
                    },
                    '&.MuiInputLabel-shrink': {
                      right: 1,
                      transform: 'translateX(13px) translateY(-7px) scale(0.75)',
                    },
                    '&.Mui-required': {
                      '&::after': {
                        display: 'none'
                      }
                    }
                  }
                }}
                sx={rtlTextFieldSx}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'row-reverse' }}>
          <Button onClick={resetCreateForm} sx={{ fontWeight: 'bold' }}>
            إلغاء
          </Button>
          <Button onClick={handleCreateGovernorate} variant="contained" color="primary" sx={{ fontWeight: 'bold' }}>
            إضافة
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShippingManagement;